import { useMemo } from "react";
import { AuthProvider as OidcProvider, useAuth as useOidcAuth } from "react-oidc-context";
import { setAuthTokenProvider } from "@repo/api-client";
import { decodeJwtClaims } from "./claims";
import { AuthStateContext } from "./context";
import type { AuthState, Identity } from "./context";
import { readAuthConfig, readAuthMode, toOidcProviderProps } from "./config";
import { StubAuthProvider } from "./stub";

/**
 * Maps the react-oidc-context session onto the vendor-neutral [AuthState] and publishes it on the
 * shared [AuthStateContext] — the same context the dev-only stub path populates, so `useAuth` never
 * branches on the mode. Also bridges the token into @repo/api-client's fetch mutator so all backend
 * calls carry a bearer token without api-client ever importing an auth SDK.
 *
 * Exported for the package's own tests; apps compose [AuthProvider] instead.
 */
export function OidcStateBridge({ children }: { children: React.ReactNode }) {
  const oidc = useOidcAuth();
  // Register the bearer-token provider DURING RENDER (via useMemo), not in an effect. A child's first
  // query (e.g. the ["me"] venue bootstrap, or the venue entitlements) fires from ITS OWN effect, and
  // child effects run BEFORE this parent's effects — so registering in a useEffect let that first /api
  // call go out token-less and 401 on a cold page load, hanging the shell. useMemo runs in the render
  // phase, ahead of every child effect, so the provider is always set first. The getter reads the token
  // live, so it still reflects silent refreshes.
  // oxlint-disable-next-line react/void-use-memo -- deliberate useMemo-as-render-phase-hook; see above.
  useMemo(() => setAuthTokenProvider(() => oidc.user?.access_token), [oidc.user]);

  const token = oidc.user?.access_token;
  const profile = oidc.user?.profile;
  const state = useMemo<AuthState>(() => {
    const user: Identity | null = profile
      ? {
          sub: String(profile.sub ?? ""),
          email: profile.email,
          name: profile.name,
        }
      : null;
    return {
      isAuthenticated: oidc.isAuthenticated,
      isLoading: oidc.isLoading,
      user,
      claims: decodeJwtClaims(token),
      login: (options) =>
        void oidc.signinRedirect(
          // Only attach extraQueryParams when a hint was asked for — passing an empty object would
          // still append `kc_idp_hint=` and Keycloak treats the empty alias as "provider not
          // found", failing the login instead of showing the chooser.
          options?.idpHint ? { extraQueryParams: { kc_idp_hint: options.idpHint } } : undefined,
        ),
      logout: () => void oidc.signoutRedirect(),
      getToken: () => oidc.user?.access_token,
      // signinSilent uses the refresh-token grant, and Keycloak re-runs the realm's protocol
      // mappers when it issues the new access token — which is exactly why this picks up claims
      // written after sign-in. A hard `signinRedirect` would work too, but it would throw away
      // the page the caller is standing on mid-flow.
      refreshClaims: async () => {
        await oidc.signinSilent();
      },
    };
  }, [oidc, token, profile]);

  return <AuthStateContext.Provider value={state}>{children}</AuthStateContext.Provider>;
}

export interface AuthProviderProps {
  children: React.ReactNode;
}

function OidcAuthProvider({ children }: AuthProviderProps) {
  const props = useMemo(() => toOidcProviderProps(readAuthConfig()), []);
  return (
    <OidcProvider {...props}>
      <OidcStateBridge>{children}</OidcStateBridge>
    </OidcProvider>
  );
}

/**
 * AuthProvider — wraps the app with auth. In the default `oidc` mode this is the real
 * auth-code + PKCE flow via env-driven config; under `VITE_AUTH_MODE=stub` (dev-only,
 * fail-closed in production builds — see readAuthMode) it swaps in the local-auth stub.
 * Both modes populate the same internal context, so consumers are mode-agnostic.
 */
export function AuthProvider({ children }: AuthProviderProps) {
  return readAuthMode() === "stub" ? (
    <StubAuthProvider>{children}</StubAuthProvider>
  ) : (
    <OidcAuthProvider>{children}</OidcAuthProvider>
  );
}
